/**
 * admin/makua/AdminRoomCalendar.js — Retreats → Booking Calendar (V2.1, Page 4;
 * was "Room calendar").
 *
 * Columns are nights; rows are the units actually sold (every dorm bed,
 * every private room), grouped by room type in the order set under
 * Accommodation. Bars are reservations (coloured by retreat type, striped
 * while unpaid) and staff blocks.
 *
 *   - Day, Week, Month or any range up to four months; the unit column
 *     stays put while the nights scroll.
 *   - Hover or focus a reservation for a short preview; click (or Enter) to
 *     open it.
 *   - Click an empty night, or drag across several, to start a reservation
 *     or block those nights; click a block to change or remove it. Every
 *     change goes through the same overlap check as bookings.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import AdminLayout from "../AdminLayout";
import { usePermissions, hasPermission } from "../permissions";
import { inventoryAPI } from "../../services/api";
import ReservationDrawer from "./ReservationDrawer";
import StaffBooking from "./StaffBooking";
import { useLoad, Field, Modal, asDate, addDays, today, ymd, typeColour, fmtDate, fmtStamp, range, money, RES_STATUS } from "./ops";
import { useStored } from "./kit";
import "./ops.css";

const KIND_LABEL = { maintenance: "Maintenance", property: "Property hold", forge: "Forge stay", cleaning: "Cleaning" };
const daysBetween = (a, b) => Math.round((asDate(b) - asDate(a)) / 86400000);
const VIEWS = [["day", "Day"], ["week", "Week"], ["month", "Month"], ["custom", "Custom"]];
const COL_W = { day: 260, week: 112 };
const monthStart = (d) => `${d.slice(0, 8)}01`;
const monthEnd = (d) => { const x = asDate(monthStart(d)); x.setMonth(x.getMonth() + 1); x.setDate(0); return ymd(x); };
const shiftMonth = (d, n) => { const x = asDate(monthStart(d)); x.setMonth(x.getMonth() + n); return ymd(x); };

export default function AdminRoomCalendar() {
  return (
    <AdminLayout pageTitle="Booking Calendar" pageSubtitle="Every bed and room, night by night: reservations, holds and blocks." permission="inventory.view" feature="reservations">
      <Calendar />
    </AdminLayout>
  );
}

function Calendar() {
  const { admin } = usePermissions();
  const canBook = hasPermission(admin, "reservations.manage");
  const canBlock = hasPermission(admin, "inventory.edit");
  const seesMoney = hasPermission(admin, "reservations.view");

  const [view, setView] = useStored("cal:view", "week");
  const [anchor, setAnchor] = useState(() => addDays(today(), -1));
  const [custom, setCustom] = useStored("cal:custom", { from: addDays(today(), -1), to: addDays(today(), 20) });
  const [typeFilter, setTypeFilter] = useStored("cal:type", "");
  const [statusFilter, setStatusFilter] = useStored("cal:status", "");
  const [sellerFilter, setSellerFilter] = useState("");
  const [unitOrder, setUnitOrder] = useStored("cal:order", "custom");
  const [folded, setFolded] = useStored("cal:folded", {});
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);          // reservation id
  const [blockEdit, setBlockEdit] = useState(null);  // an existing block bar
  const [pick, setPick] = useState(null);          // { row, start, end, x, y } an empty selection
  const [newBlock, setNewBlock] = useState(null);
  const [newRes, setNewRes] = useState(null);
  const [tip, setTip] = useState(null);            // { bar, rect }

  const { from, to } = useMemo(() => {
    if (view === "day") return { from: anchor, to: anchor };
    if (view === "week") return { from: anchor, to: addDays(anchor, 6) };
    if (view === "month") return { from: monthStart(anchor), to: monthEnd(anchor) };
    const f = custom.from || today(); let t = custom.to && custom.to >= f ? custom.to : addDays(f, 13);
    if (daysBetween(f, t) > 119) t = addDays(f, 119);
    return { from: f, to: t };
  }, [view, anchor, custom]);
  const span = daysBetween(from, to) + 1;
  const step = (dir) => {
    if (view === "day") setAnchor(addDays(anchor, dir));
    else if (view === "week") setAnchor(addDays(anchor, 7 * dir));
    else if (view === "month") setAnchor(shiftMonth(anchor, dir));
    else setCustom({ from: addDays(from, span * dir), to: addDays(to, span * dir) });
  };
  const goToday = () => {
    const t = today();
    if (view === "custom") setCustom({ from: addDays(t, -1), to: addDays(t, span - 2) });
    else setAnchor(view === "day" ? t : view === "month" ? monthStart(t) : addDays(t, -1));
  };

  const { data, loading, error, reload } = useLoad(() => inventoryAPI.calendar(from, to), [from, to]);
  const days = useMemo(() => Array.from({ length: span }, (_, i) => addDays(from, i)), [from, span]);
  const sellers = useMemo(() => [...new Set((data?.bars || []).map((b) => b.preview?.seller).filter(Boolean))].sort(), [data]);

  const needle = q.trim().toLowerCase();
  const barMatches = (b) => {
    if (typeFilter === "blocks" && b.reservationId) return false;
    if (typeFilter && typeFilter !== "blocks" && String(b.typeId) !== typeFilter) return false;
    if (statusFilter === "blocks" && b.reservationId) return false;
    if (statusFilter && statusFilter !== "blocks" && b.resStatus !== statusFilter) return false;
    if (sellerFilter && b.preview?.seller !== sellerFilter) return false;
    return true;
  };
  const textMatch = (b) => !needle || `${b.preview?.guest || b.label} ${b.ref || ""} ${b.retreat || ""} ${b.note || ""}`.toLowerCase().includes(needle);
  const bars = useMemo(() => (data?.bars || []).filter(barMatches), [data, typeFilter, statusFilter, sellerFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Rows: a group row per room type, then its units (unless folded or filtered out). */
  const layout = useMemo(() => {
    const rows = []; const rowOf = new Map(); let r = 2;
    const filtering = !!needle;
    (data?.groups || []).forEach((g) => {
      let units = g.rows;
      if (unitOrder === "name") units = [...units].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
      if (filtering) {
        units = units.filter((u) => u.label.toLowerCase().includes(needle) || g.name.toLowerCase().includes(needle)
          || bars.some((b) => b.rowKeys.includes(u.key) && textMatch(b)));
        if (!units.length) return;
      }
      rows.push({ kind: "group", g, r: r++, count: g.rows.length });
      if (folded[g.id] && !filtering) return;
      units.forEach((u) => { rowOf.set(u.key, r); rows.push({ kind: "unit", u, g, r: r++ }); });
    });
    return { rows, rowOf };
  }, [data, unitOrder, folded, needle, bars]); // eslint-disable-line react-hooks/exhaustive-deps

  const col = (d) => daysBetween(from, d) + 2;
  const colW = COL_W[view] || 46;
  const wide = colW >= 100;

  /* Drag across empty nights in one row to choose a range. */
  const drag = useRef(null);
  const [sel, setSel] = useState(null);
  const startSel = (row, d, e) => {
    if (!canBook && !canBlock) return;
    drag.current = { row, a: d }; setSel({ key: row.key, a: d, b: d });
    e.preventDefault();
  };
  const moveSel = (row, d) => { if (drag.current && drag.current.row.key === row.key) setSel({ key: row.key, a: drag.current.a, b: d }); };
  const endSel = (e) => {
    if (!drag.current || !sel) return;
    const [s, t] = [sel.a, sel.b].sort();
    setPick({ row: drag.current.row, start: s, end: addDays(t, 1), x: e.clientX, y: e.clientY });
    drag.current = null;
  };
  useEffect(() => {
    const up = () => { if (drag.current) { drag.current = null; setSel(null); } };
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }, []);
  const clearPick = () => { setPick(null); setSel(null); };

  const legendTypes = (data?.types || []).filter((t) => bars.some((b) => b.typeId === t.id));

  return (
    <div className="ops">
      <div className="ops-bar ops-toolbar">
        <div className="ops-actions" style={{ justifyContent: "flex-start" }}>
          <button className="ops-btn" aria-label={`Earlier ${view === "custom" ? "range" : view}`} onClick={() => step(-1)}>‹</button>
          <button className="ops-btn" onClick={goToday}>Today</button>
          <button className="ops-btn" aria-label={`Later ${view === "custom" ? "range" : view}`} onClick={() => step(1)}>›</button>
        </div>
        <div className="ops-field"><span>View</span>
          <div className="ops-seg" role="group" aria-label="View">
            {VIEWS.map(([k, l]) => <button key={k} type="button" aria-pressed={view === k} onClick={() => { if (k === "custom") setCustom({ from, to }); if (k === "month") setAnchor(monthStart(from)); else if (view === "month") setAnchor(from); setView(k); }}>{l}</button>)}
          </div>
        </div>
        {view === "custom" ? (
          <>
            <Field label="From"><input type="date" value={custom.from} onChange={(e) => e.target.value && setCustom({ ...custom, from: e.target.value })} /></Field>
            <Field label="To" hint="Up to 4 months"><input type="date" value={custom.to} min={custom.from} onChange={(e) => e.target.value && setCustom({ ...custom, to: e.target.value })} /></Field>
          </>
        ) : (
          <Field label={view === "month" ? "Month" : "From"}>
            {view === "month"
              ? <input type="month" value={from.slice(0, 7)} onChange={(e) => e.target.value && setAnchor(`${e.target.value}-01`)} />
              : <input type="date" value={anchor} onChange={(e) => e.target.value && setAnchor(e.target.value)} />}
          </Field>
        )}
        <span className="ops-count">{view === "day" ? fmtDate(from) : range(from, to)} · {span} night{span === 1 ? "" : "s"}</span>
        <span className="ops-spacer" />
        {canBook && <button className="ops-btn ops-btn--primary" onClick={() => setNewRes({})}>New reservation</button>}
      </div>
      <div className="ops-bar ops-toolbar">
        <Field label="Find" className="ops-search"><input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Guest, MAK-…, room or bed" /></Field>
        <Field label="Retreat">
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">Everything</option>
            {(data?.types || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            <option value="blocks">Blocks only</option>
          </select>
        </Field>
        <Field label="Status">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">Any</option><option value="confirmed">Confirmed</option><option value="pending_payment">Held, unpaid</option><option value="blocks">Blocks</option>
          </select>
        </Field>
        {sellers.length > 0 && (
          <Field label="Seller">
            <select value={sellerFilter} onChange={(e) => setSellerFilter(e.target.value)}><option value="">Any</option>{sellers.map((s) => <option key={s}>{s}</option>)}</select>
          </Field>
        )}
        <Field label="Units" hint="Custom is the Accommodation order">
          <select value={unitOrder} onChange={(e) => setUnitOrder(e.target.value)}><option value="custom">Custom order</option><option value="name">By name</option></select>
        </Field>
        <span className="ops-spacer" />
        <button className="ops-btn ops-btn--sm" onClick={() => setFolded(Object.fromEntries((data?.groups || []).map((g) => [g.id, true])))}>Fold all</button>
        <button className="ops-btn ops-btn--sm" onClick={() => setFolded({})}>Unfold all</button>
      </div>

      <div className="ops-cal-legend" aria-label="Legend">
        {legendTypes.map((t) => <span key={t.id}><i style={{ background: typeColour(t.id) }} />{t.name}</span>)}
        <span><i style={{ background: "#d9d4c8", backgroundImage: "repeating-linear-gradient(135deg, rgba(255,255,255,.6) 0 3px, transparent 3px 6px)" }} />Held, unpaid</span>
        <span><i style={{ background: "#f4c7c3" }} />Maintenance</span>
        <span><i style={{ background: "#cfe3f5" }} />Cleaning</span>
        <span><i style={{ background: "#e5e0d6" }} />Property hold</span>
        <span><i style={{ background: "#fbd38d" }} />Forge stay</span>
        {seesMoney && <span><b style={{ color: "#15803d" }}>●</b> paid · <b style={{ color: "#b45309" }}>●</b> part paid · <b style={{ color: "#b91c1c" }}>●</b> unpaid · ✓ cleared</span>}
        {(canBook || canBlock) && <span className="ops-muted">Click a free night, or drag across several, to book or block them.</span>}
      </div>

      {error && <p className="ops-error">{error}</p>}
      {loading && !data && <p className="ops-muted">Loading…</p>}
      {data && !data.groups.length && <p className="ops-empty">No rooms set up yet. Add them under Accommodation.</p>}
      {data && data.groups.length > 0 && !layout.rows.length && <p className="ops-note">Nothing matches "{q}" in these dates.</p>}

      {data && layout.rows.length > 0 && (
        <div className="ops-cal-wrap" onMouseLeave={() => setTip(null)}>
          <div className={`ops-cal ${wide ? "ops-cal--wide" : ""}`} style={{ gridTemplateColumns: `13rem repeat(${span}, ${wide ? `minmax(${colW}px, 1fr)` : `${colW}px`})` }} role="group"
            aria-label={`Rooms and beds from ${fmtDate(from)} to ${fmtDate(to)}`} onMouseUp={endSel}>
            <div className="ops-cal-corner" style={{ gridRow: 1, gridColumn: 1 }}>Unit</div>
            {days.map((d, i) => {
              const dt = asDate(d); const wk = dt.getDay() === 0 || dt.getDay() === 6;
              return (
                <div key={d} className={`ops-cal-day ${wk ? "is-weekend" : ""} ${d === today() ? "is-today" : ""}`} style={{ gridRow: 1, gridColumn: i + 2 }}>
                  {dt.toLocaleDateString("en-US", { weekday: "short" })}<b>{dt.getDate()}</b>{(wide || dt.getDate() === 1) && <small>{dt.toLocaleDateString("en-US", { month: "short" })}</small>}
                </div>
              );
            })}

            {layout.rows.map((row) => (row.kind === "group" ? (
              <button key={`g${row.g.id}`} type="button" className="ops-cal-group" style={{ gridRow: row.r }}
                aria-expanded={!folded[row.g.id] || !!needle} onClick={() => setFolded({ ...folded, [row.g.id]: !folded[row.g.id] })}>
                <span className="ops-cal-group-label"><span aria-hidden="true">{folded[row.g.id] && !needle ? "▸" : "▾"}</span> {row.g.name} <span className="ops-cal-group-n">{row.count} {row.g.sellBy === "bed" ? "beds" : "rooms"}</span></span>
              </button>
            ) : (
              <Unit key={row.u.key} row={row} days={days} sel={sel && sel.key === row.u.key ? sel : null}
                onDown={(d, e) => startSel({ ...row.u, categoryId: row.g.id }, d, e)} onEnter={(d) => moveSel(row.u, d)} />
            )))}

            {bars.flatMap((b) => b.rowKeys.filter((k) => layout.rowOf.has(k)).map((k) => {
              const s = Math.max(2, col(b.start)); const e = Math.min(span + 2, col(b.end));
              if (e <= s) return null;
              const isRes = !!b.reservationId;
              const dim = needle && !textMatch(b) && !layout.rows.some((r) => r.kind === "unit" && r.u.key === k && r.u.label.toLowerCase().includes(needle));
              const dot = !seesMoney ? null : b.payment === "paid" ? "#15803d" : b.payment === "part_paid" ? "#b45309" : b.payment === "unpaid" ? "#b91c1c" : null;
              const name = isRes ? (wide && b.preview?.guest ? `${b.preview.guest}${b.preview.party > 1 ? ` +${b.preview.party - 1}` : ""}` : b.label) : `${KIND_LABEL[b.kind]}${b.note ? ` · ${b.note}` : ""}`;
              const show = (ev) => setTip({ bar: b, rect: ev.currentTarget.getBoundingClientRect() });
              return (
                <button key={`${b.id}-${k}`} type="button"
                  className={`ops-cal-bar ${isRes ? "" : `ops-cal-bar--${b.kind}`} ${b.status === "held" ? "is-held" : ""} ${dim ? "is-dim" : ""}`}
                  style={{ gridRow: layout.rowOf.get(k), gridColumn: `${s} / ${e}`, ...(isRes ? { backgroundColor: typeColour(b.typeId) } : {}) }}
                  aria-label={isRes ? `${b.preview?.guest || b.label}, ${b.ref}, ${range(b.start, b.end)}` : `${KIND_LABEL[b.kind]}, ${range(b.start, b.end)}${b.note ? `, ${b.note}` : ""}`}
                  aria-describedby={tip?.bar.id === b.id ? "cal-tip" : undefined}
                  onMouseEnter={show} onFocus={show} onMouseLeave={() => setTip(null)} onBlur={() => setTip(null)}
                  onClick={() => { setTip(null); if (isRes) setOpen(b.reservationId); else setBlockEdit(b); }}>
                  {dot && <span className="dot" style={{ background: dot }} />}
                  {name}{isRes && seesMoney && b.screening === "approved" ? " ✓" : ""}
                </button>
              );
            }))}
          </div>
        </div>
      )}

      {tip && <Preview tip={tip} seesMoney={seesMoney} />}
      {pick && (
        <CellMenu pick={pick} canBook={canBook} canBlock={canBlock} onClose={clearPick}
          onBook={() => { setNewRes({ unit: pick.row, date: pick.start, end: pick.end }); clearPick(); }}
          onBlock={() => { setNewBlock({ row: pick.row, start: pick.start, end: pick.end }); clearPick(); }} />
      )}
      {open && <ReservationDrawer id={open} onClose={() => setOpen(null)} onChanged={reload} />}
      {newRes && <StaffBooking presetUnit={newRes.unit} presetDate={newRes.date} presetEnd={newRes.end} onClose={() => setNewRes(null)} onCreated={(r) => { setNewRes(null); reload(); setOpen(r.id); }} />}
      {newBlock && <NewBlockModal block={newBlock} onClose={() => setNewBlock(null)} onDone={() => { setNewBlock(null); reload(); }} />}
      {blockEdit && <EditBlockModal bar={blockEdit} groups={data?.groups || []} canEdit={canBlock} onClose={() => setBlockEdit(null)} onDone={() => { setBlockEdit(null); reload(); }} />}
    </div>
  );
}

const Unit = ({ row, days, sel, onDown, onEnter }) => {
  const [a, b] = sel ? [sel.a, sel.b].sort() : [null, null];
  return (
    <>
      <div className="ops-cal-unit" style={{ gridRow: row.r, gridColumn: 1 }} title={row.u.area || ""}>{row.u.label}</div>
      {days.map((d, i) => {
        const wk = [0, 6].includes(asDate(d).getDay());
        const on = sel && d >= a && d <= b;
        return (
          <div key={d} className={`ops-cal-cell ${wk ? "is-weekend" : ""} ${on ? "is-sel" : ""}`} style={{ gridRow: row.r, gridColumn: i + 2 }}
            onMouseDown={(e) => onDown(d, e)} onMouseEnter={() => onEnter(d)} />
        );
      })}
    </>
  );
};

/** The hover/focus card for a bar. Money and screening only for staff who may see them. */
function Preview({ tip, seesMoney }) {
  const b = tip.bar; const p = b.preview || {};
  const top = Math.min(window.innerHeight - 220, tip.rect.bottom + 6);
  const left = Math.min(window.innerWidth - 300, Math.max(8, tip.rect.left));
  const nights = daysBetween(b.start, b.end);
  return (
    <div id="cal-tip" role="tooltip" className="ops-cal-tip" style={{ top, left }}>
      {b.reservationId ? (
        <>
          <b>{p.guest || b.label}</b> <span className="ops-muted">{b.ref}</span>
          <div>{fmtDate(p.arrive || b.start)} – {fmtDate(p.depart || b.end)} · {daysBetween(p.arrive || b.start, p.depart || b.end)} nights · {p.party} {p.party === 1 ? "guest" : "guests"}</div>
          <div>{p.unit}{b.typeName ? ` · ${b.typeName}` : ""}</div>
          <div>{RES_STATUS[b.resStatus]?.label || b.resStatus}{b.resStatus === "pending_payment" && p.holdExpires ? ` until ${fmtStamp(p.holdExpires)}` : ""}</div>
          {seesMoney && <div>Paid {money(p.paid)} of {money(p.total)}{p.balance > 0 ? ` · balance ${money(p.balance)}` : ""}</div>}
          {seesMoney && b.screening && <div>Screening: {b.screening.replace("_", " ")}</div>}
          <div className="ops-muted">{p.seller ? `Seller: ${p.seller}` : p.source === "web" ? "Booked on the website" : p.source === "admin" ? "Booked by staff" : ""}</div>
          <div className="ops-muted">Click or press Enter to open</div>
        </>
      ) : (
        <>
          <b>{KIND_LABEL[b.kind] || b.kind}</b>
          <div>{p.unit} · {fmtDate(b.start)} – {fmtDate(b.end)} ({nights} night{nights === 1 ? "" : "s"})</div>
          {b.note && <div>{b.note}</div>}
          {p.expires && <div>Lapses {fmtStamp(p.expires)}</div>}
          {p.by && <div className="ops-muted">Added by {p.by}</div>}
          <div className="ops-muted">Click to change or remove</div>
        </>
      )}
    </div>
  );
}

/** After choosing free nights: book them, or block them. */
function CellMenu({ pick, canBook, canBlock, onClose, onBook, onBlock }) {
  const nights = daysBetween(pick.start, pick.end);
  const ref = useRef(null);
  useEffect(() => { ref.current?.querySelector("button")?.focus(); }, []);
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  return (
    <>
      <div className="ops-pop-scrim" onClick={onClose} />
      <div ref={ref} className="ops-pop" role="menu" aria-label="Free nights" style={{ top: Math.min(window.innerHeight - 160, pick.y + 8), left: Math.min(window.innerWidth - 270, pick.x) }}>
        <p className="ops-muted" style={{ margin: "0 0 0.4rem" }}><b>{pick.row.label}</b><br />{fmtDate(pick.start)} – {fmtDate(pick.end)} · {nights} night{nights === 1 ? "" : "s"}</p>
        {canBook && <button type="button" role="menuitem" className="ops-btn ops-btn--sm ops-btn--primary" onClick={onBook}>New reservation here</button>}
        {canBlock && <button type="button" role="menuitem" className="ops-btn ops-btn--sm" onClick={onBlock}>Block {pick.row.bedId ? "this bed" : "this room"}</button>}
        <button type="button" role="menuitem" className="ops-btn ops-btn--sm" onClick={onClose}>Cancel</button>
      </div>
    </>
  );
}

function NewBlockModal({ block, onClose, onDone }) {
  const [f, setF] = useState({ kind: "maintenance", start_date: block.start, end_date: block.end, label: "", wholeRoom: !block.row.bedId, expires_at: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await inventoryAPI.block({ room_id: block.row.roomId, bed_id: f.wholeRoom ? null : block.row.bedId, start_date: f.start_date, end_date: f.end_date, kind: f.kind, label: f.label, expires_at: f.expires_at || null });
      toast.success("Blocked."); onDone();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Block ${block.row.label}`} onClose={onClose}>
      <p className="ops-muted">Blocked nights can't be sold. The same rule as bookings applies: if anyone is in this unit on these nights, you'll be told and nothing is saved.</p>
      <BlockFields f={f} setF={setF} bedLabel={block.row.bedId ? block.row.label : null} />
      <div className="ops-actions"><button className="ops-btn" onClick={onClose}>Cancel</button>
        <button className="ops-btn ops-btn--primary" disabled={busy || !f.start_date || !f.end_date || f.end_date <= f.start_date} onClick={save}>Block</button></div>
    </Modal>
  );
}

const BlockFields = ({ f, setF, bedLabel, disabled }) => (
  <fieldset disabled={disabled} style={{ border: 0, padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.6rem" }}>
    <div className="ops-grid ops-grid--2">
      <Field label="Why">
        <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>{Object.entries(KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
      </Field>
      <Field label="Note"><input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="Leaking shower" /></Field>
      <Field label="First night"><input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></Field>
      <Field label="Free again on" hint="The morning it's back in use"><input type="date" value={f.end_date} min={f.start_date ? addDays(f.start_date, 1) : undefined} onChange={(e) => setF({ ...f, end_date: e.target.value })} /></Field>
      <Field label="Lapses on its own" hint="Optional: for a provisional hold" className="ops-span-all"><input type="datetime-local" value={f.expires_at} onChange={(e) => setF({ ...f, expires_at: e.target.value })} /></Field>
    </div>
    {bedLabel && <label className="ops-check"><input type="checkbox" checked={f.wholeRoom} onChange={(e) => setF({ ...f, wholeRoom: e.target.checked })} />Block the whole room, not just {bedLabel.split(" · ").pop()}</label>}
  </fieldset>
);

/** An existing block: change its dates, kind, note, room-or-bed and expiry, or remove it. */
function EditBlockModal({ bar, groups, canEdit, onClose, onDone }) {
  const p = bar.preview || {};
  // The bed rows of this room, to offer "just one bed" when it's a dorm.
  const roomBeds = groups.flatMap((g) => g.rows).filter((r) => r.roomId === p.roomId && r.bedId);
  const [f, setF] = useState({
    kind: bar.kind, label: bar.note || "", start_date: bar.start, end_date: bar.end,
    expires_at: p.expires ? p.expires.replace(" ", "T").slice(0, 16) : "", wholeRoom: !p.bedId, bed_id: p.bedId || roomBeds[0]?.bedId || null,
  });
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); toast.success(r?.message || ok); onDone(); } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const save = () => run(() => inventoryAPI.updateBlock(bar.id, {
    kind: f.kind, label: f.label, start_date: f.start_date, end_date: f.end_date, expires_at: f.expires_at || null,
    room_id: p.roomId, bed_id: f.wholeRoom ? null : f.bed_id,
  }), "Block updated.");
  return (
    <Modal title={`${KIND_LABEL[bar.kind] || "Block"} · ${p.unit || ""}`} onClose={onClose}>
      <p className="ops-muted" style={{ margin: 0 }}>{range(bar.start, bar.end)}{p.by ? ` · added by ${p.by}` : ""}. A change is checked against every booking and block first.</p>
      <BlockFields f={f} setF={setF} bedLabel={null} disabled={!canEdit} />
      {roomBeds.length > 0 && (
        <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0 }}>
          <div className="ops-grid ops-grid--2">
            <label className="ops-check"><input type="checkbox" checked={f.wholeRoom} onChange={(e) => setF({ ...f, wholeRoom: e.target.checked })} />The whole room</label>
            {!f.wholeRoom && (
              <Field label="Just this bed">
                <select value={f.bed_id || ""} onChange={(e) => setF({ ...f, bed_id: Number(e.target.value) })}>{roomBeds.map((r) => <option key={r.bedId} value={r.bedId}>{r.label}</option>)}</select>
              </Field>
            )}
          </div>
        </fieldset>
      )}
      {canEdit ? (
        <div className="ops-actions">
          <button className="ops-btn ops-btn--danger" style={{ marginRight: "auto" }} disabled={busy}
            onClick={() => window.confirm("Remove this block? The nights go back on sale.") && run(() => inventoryAPI.release(bar.id), "Removed.")}>Remove block</button>
          <button className="ops-btn" onClick={onClose}>Cancel</button>
          <button className="ops-btn ops-btn--primary" disabled={busy || !f.start_date || !f.end_date || f.end_date <= f.start_date} onClick={save}>Save changes</button>
        </div>
      ) : <div className="ops-actions"><button className="ops-btn" onClick={onClose}>Close</button></div>}
    </Modal>
  );
}
